// @flow
import Spinner from 'esm-web-pro/components/forms/Spinner';
import * as React from 'react';
import { connect } from 'react-redux';
import { Box, Button } from 'grommet';
import type { IntlShape } from 'react-intl';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import type { ApiClient, Confirmation } from 'speed-js-react';
import {
    connectAPI, withConfirmation, withRouter, getUser as getUserSelector,
} from 'speed-js-react';
import { get, isEqual } from 'lodash';
import memoizeOne from 'memoize-one';
import styled from 'styled-components';
import { bindActionCreators } from 'redux';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import MapEditorSelectorButtons from 'esm-web-pro/containers/map/MapEditorSelectorButtons';
import ConfirmBeforeUnload from 'esm-web-pro/components/dialogs/ConfirmBeforeUnload';
import connectWS from 'speed-js-react/src/websocket/connectWS';
import ContextNavbar from 'esm-web-pro/components/layout/ContextNavbar';
import EITExport from 'esm-web-pro/containers/pages/network/EITExport';
import { CONTEXT_NAVBAR_HEIGHT } from 'esm-web-pro/styles/theme';
import Menu from 'esm-web-pro/components/Menu';
import { generatePath } from 'react-router';
import MapSearchField from 'esm-web-pro/components/maps/MapSearchField';
import type { Map } from './types';
import MapViewers from './MapViewers';

const messages = defineMessages({
    'discard-draft-question': {
        id: 'discard-draft-question',
        defaultMessage: 'Are you sure you want to discard the draft?',
    },
    'discard-draft-yes': {
        id: 'discard-draft-yes',
        defaultMessage: 'Yes',
    },
    'discard-draft-no': {
        id: 'discard-draft-no',
        defaultMessage: 'No',
    },
    'activate-draft-question': {
        id: 'activate-draft-question',
        defaultMessage: 'Are you sure you want to activate the draft?',
    },
    'activate-draft-yes': {
        id: 'activate-draft-yes',
        defaultMessage: 'Yes',
    },
    'activate-draft-no': {
        id: 'activate-draft-no',
        defaultMessage: 'No',
    },
});

type SidePanelActionProps = {
    closeAllPanels: (skipSensitivity?: boolean) => { },
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    children: (state: Object) => React.Node,
    maps: Array<Map>,
    draftMap: Map,
    mapType: string,
    typeMaps: (mapType: string) => Promise<Object>,
    getDraftMapByIdentity: (identity: string, mapType: string) => Promise<Object>,
    // getDraftMapByUUID: (uuid: string) => Promise<Object>,
    // eslint-disable-next-line react/no-unused-prop-types
    createNewDraft: (identity: string) => Promise<Object>,
    activateDraft: (uuid: string) => Promise<Object>,
    discardDraft: (uuid: string) => Promise<Object>,
    confirm: (confirmation: Confirmation) => void,
    intl: IntlShape,
    permissions: Array<string>,
    match: Object,
    history: Object,
    mapAdditionalActions?: React.Node,
    currentUser: Object | null,
}

type State = {
    currentMap: Object | null,
    activeMap: Object | null,
    zoom: number | null,
    center: [number, number] | null,
    loading: boolean,
    mapLoading: boolean,
    editedByOtherUsers: boolean,
};

const StyledBox = styled(Box)`
    position: relative;
`;

const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);

@connectWS
@connect((state: Object) => ({
    currentUser: getUserSelector(state),
}), mapDispatchToProps)
@withRouter
@withConfirmation
@connectAPI((apiClient: ApiClient, props: Props) => {
    const resourceType = props.mapType === 'coverage' ? 'address' : 'networks';
    return {
        typeMaps: {
            send: (mapType) => (
                apiClient.resources[`${resourceType}.map`].list({
                    type: mapType,
                    valid_to__isnull: 'True',
                    valid_from__isnull: 'False',
                })
            ),
            success: (response: {results: Object }) => {
                if (response.results) {
                    return { maps: response.results };
                }
                return null;
            },
        },
        getDraftMapByIdentity: {
            send: (identity, mapType) => (
                apiClient.resources[`${resourceType}.map`].list({
                    identity,
                    type: mapType,
                    valid_to__isnull: 'True',
                    valid_from__isnull: 'True',
                })
            ),
            success: (response: {results: Object }) => ({ draftMap: response.results[0] || null }),
        },
        getDraftMapByUUID: {
            send: (mapUuid) => (
                apiClient.resources[`${resourceType}.map`].get(mapUuid)
            ),
            success: (response: {result: Object }) => ((response.is_draft) && ({ draftMap: response || null })
            ),
        },
        createNewDraft: {
            send: (uuid) => (
                apiClient.resources[`${resourceType}.map`].draft(uuid, {})
            ),
            success: (response: {result: Object }) => ({ draftMap: response.result || null }),
        },
        activateDraft: {
            send: (uuid) => apiClient.resources[`${resourceType}.map`].activate(uuid, {}),
            success: (response: {result: Object }) => ({ activeMap: response.result || null }),
        },
        discardDraft: {
            send: (uuid) => (apiClient.resources[`${resourceType}.map`].discard(uuid, {})),
            success: () => ({ draftMap: null }),
        },
    };
})
class MapEditorSelector extends React.Component<Props, State> {
    currentMap: Map;

    dropButtonRef: { current: React.Ref<Button> | null};

    _isMounted = false;

    constructor(props: Props) {
        super(props);
        this.state = {
            currentMap: null,
            activeMap: null,
            loading: false,
            zoom: null,
            center: null,
            mapLoading: false,
            editedByOtherUsers: true,
        };

        this.getChoices = memoizeOne(this.getChoices);
        this.dropButtonRef = React.createRef();
    }

    componentDidMount(): void {
        this._isMounted = true;
        this.openMap();
    }

    componentDidUpdate(prevProps) {
        const { match: { params: { mapIdentity } }, confirm, confirmations } = this.props;
        if (!mapIdentity && prevProps.match.params.mapIdentity) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ currentMap: null });
        } else if (mapIdentity !== prevProps.match.params.mapIdentity) {
            this.openMap();
        }

        const newMap = this.checkForNewActiveMap();
        if (newMap && !confirmations.length) {
            confirm({
                message: <FormattedMessage
                    id="map-activated-by-other-user"
                    defaultMessage="The map you are currently viewing has been edited.
                        Click 'OK' to load its latest version."
                />,
                actions: {
                    confirm: {
                        label: 'OK',
                        callback: () => this.handleChange(newMap.identity, true, this.getPathExtend()),
                        type: 'positive',
                    },
                },
            });
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.setState({ currentMap: null, activeMap: null });
    }

    getChoices = (maps: Array<Map>, currentMap) => maps.filter((m) => currentMap?.identity !== m.identity).map((m) => ({
        label: m.name,
        onClick: () => this.redirect(m.identity),
    }));

    openMap = () => {
        const {
            mapType, typeMaps, match: { params: { mapIdentity, versionType } }, getDraftMapByIdentity,
        } = this.props;

        typeMaps(mapType).then((response) => {
            if (!this._isMounted) {
                return;
            }
            if (mapIdentity) {
                if (versionType !== 'draft') {
                    this.openActiveMap(mapIdentity);
                    if (versionType !== 'active') {
                        this.redirect(mapIdentity);
                    }
                } else {
                    getDraftMapByIdentity(mapIdentity, mapType).then((res) => {
                        if (res.count === 1 && res.results[0].is_draft) {
                            this.openDraftMap();
                        } else {
                            const map = response.results.find((m) => m.identity === mapIdentity);
                            if (map) {
                                this.openActiveMap(mapIdentity);
                                this.redirect(mapIdentity, null, this.getPathExtend());
                            }
                        }
                    });
                }
            }
        });
    };

    openActiveMap = (selectedMapIdentity) => {
        const {
            getDraftMapByIdentity, mapType, maps,
        } = this.props;
        const { activeMap } = this.state;
        const activeInitialMap = maps.find((m) => m.identity === selectedMapIdentity);
        if (activeInitialMap) {
            this.setMap(activeInitialMap);
            this.setState({ activeMap: activeInitialMap });
            // Fetch the draft map while the active one is showed
            getDraftMapByIdentity(activeInitialMap.identity, mapType);
            if (!isEqual(activeInitialMap, activeMap)) {
                this.setPosition(null, null);
            }
        }
    };

    openDraftMap = () => {
        if (!this._isMounted) {
            return;
        }

        const { draftMap, maps } = this.props;
        const currentMap = draftMap;

        const activeMap = maps.find((m) => m.identity === currentMap?.identity);

        if (currentMap) {
            this.setMap(currentMap);
            this.setState({ activeMap });
        }
    };

    handleChange = (selectedMapIdentity, redirect = true, extendPath = '') => {
        const {
            maps, getDraftMapByIdentity, mapType,
        } = this.props;
        const currentMap = maps.find((m) => m.identity === selectedMapIdentity);
        if (currentMap) {
            const { activeMap } = this.state;
            if (redirect) {
                this.redirect(currentMap.identity, null, extendPath);
            }
            this.setMap(currentMap);
            this.setState({ activeMap: currentMap });

            // Fetch the draft map while the active one is showed
            getDraftMapByIdentity(currentMap.identity, mapType);

            if (activeMap && activeMap.identity !== currentMap.identity) {
                this.setPosition(null, null);
            }
        }
    };

    checkForNewActiveMap = () => {
        // Check if the map has been updated by a different user
        const { activeMap, currentMap, editedByOtherUsers } = this.state;
        if (!editedByOtherUsers) {
            const { maps } = this.props;

            if (activeMap) {
                const newMap = maps.find((m) => m.identity === activeMap.identity);
                if (newMap && currentMap && newMap.uuid !== currentMap.uuid && newMap.uuid !== activeMap.uuid) {
                    return newMap;
                }
            }
        }
        return false;
    };

    handleEditFromOtherUser = (users) => {
        const { currentUser } = this.props;
        const editedBy = users?.active || (currentUser && users?.id !== currentUser.uuid) ? users : null;
        this.setState({
            editedByOtherUsers: !!editedBy,
        }, () => {
            const { editedByOtherUsers, activeMap } = this.state;

            if (!editedByOtherUsers) {
                const { mapType, typeMaps } = this.props;
                if (activeMap) {
                    typeMaps(mapType);
                }
            }
        });
    };

    setMap = (map) => {
        this.setState({ currentMap: map });
    };

    redirect = (mapIdentity, versionType = null, extendPath = '') => {
        const { match, history } = this.props;
        const params = { mapIdentity };
        params.versionType = versionType ? 'draft' : 'active';
        const path = generatePath(match.path, params);
        history.push(path + extendPath);
    };

    showDraft = () => {
        const { draftMap } = this.props;
        this.redirect(draftMap.identity, true);
        this.setMap(draftMap);
    };

    newDraft = () => {
        const { createNewDraft } = this.props;
        const { currentMap } = this.state;
        if (currentMap) {
            createNewDraft(currentMap.uuid).then((response) => {
                const draftMap = response.result || null;
                if (draftMap) {
                    this.redirect(draftMap.identity, true);
                    this.setMap(draftMap);
                }
            });
        }
    };

    activateDraft = () => {
        const { confirm, intl: { formatMessage } } = this.props;

        confirm({
            message: formatMessage(messages['activate-draft-question']),
            actions: {
                cancel: {
                    label: formatMessage(messages['activate-draft-no']),
                    callback: () => {},
                    type: 'negative',
                },
                confirm: {
                    label: formatMessage(messages['activate-draft-yes']),
                    callback: () => {
                        const { currentMap } = this.state;
                        const { activateDraft, mapType, typeMaps } = this.props;
                        this.setState({ loading: true });
                        if (currentMap) {
                            activateDraft(currentMap.uuid).then((response) => {
                                const activeMap = response.result ? response.result : null;
                                if (activeMap) {
                                    typeMaps(mapType).then(() => this.handleChange(activeMap.identity));
                                }
                                this.setState({ loading: false });
                            });
                        }
                    },
                    type: 'positive',
                },
            },
        });
    };

    discardDraft = () => {
        const { confirm, intl: { formatMessage }, closeAllPanels } = this.props;

        confirm({
            message: formatMessage(messages['discard-draft-question']),
            actions: {
                cancel: {
                    label: formatMessage(messages['discard-draft-no']),
                    callback: () => {},
                    type: 'negative',
                },
                confirm: {
                    label: formatMessage(messages['discard-draft-yes']),
                    callback: () => {
                        const { discardDraft, mapType, typeMaps } = this.props;
                        const { currentMap, activeMap } = this.state;
                        this.setState({ loading: true });
                        if (currentMap) {
                            closeAllPanels(true);
                            discardDraft(currentMap.uuid).then(() => {
                                if (activeMap) {
                                    typeMaps(mapType).then(() => {
                                        this.handleChange(activeMap.identity, false);
                                        this.redirect(activeMap.identity);
                                    });
                                }
                                this.setState({ loading: false });
                            });
                        }
                    },
                    type: 'positive',
                },
            },
        });
    };

    setPosition = (center, zoom) => {
        this.setState({ center, zoom });
    };

    setMapLoading = (mapLoading) => {
        this.setState({ mapLoading });
    };

    getPathExtend = () => {
        const { location } = this.props;
        const [path, extend] = location.pathname.split('/layer');
        return extend ? `/layer${extend}` : '';
    };

    handleMapLocked = () => {
        const { mapType, typeMaps } = this.props;
        const { currentMap, activeMap } = this.state;
        if (currentMap) {
            if (activeMap) {
                typeMaps(mapType).then(() => {
                    this.handleChange(activeMap.identity, false);
                    this.redirect(activeMap.identity, null, this.getPathExtend());
                    this.setState({ editedByOtherUsers: true });
                });
            }
        }
    };

    render() {
        const {
            currentMap, activeMap, loading, zoom, center, mapLoading, editedByOtherUsers,
        } = this.state;
        const {
            children, maps, draftMap, permissions, mapAdditionalActions,
        } = this.props;

        if (!maps) {
            return null;
        }
        const choices = this.getChoices(maps, currentMap);

        return (
            <Box height="100%">
                {(currentMap && draftMap === currentMap) && <ConfirmBeforeUnload />}
                <ContextNavbar
                    direction="row"
                    gap="small"
                    align="center"
                    justify="between"
                >
                    <Menu
                        label={currentMap ? currentMap.name : (
                            <FormattedMessage
                                id="map-select-default"
                                defaultMessage="Select map..."
                            />
                        )}
                        choices={choices}
                    />
                    { currentMap && (
                        <Box align="center" direction="row" justify="center" width="30%" style={{ maxWidth: '600px' }}>
                            <MapSearchField
                                searchIndex="Search..."
                                currentMap={currentMap}
                            />
                        </Box>
                    )}
                    <Box direction="row" gap="small" align="center">
                        <MapEditorSelectorButtons
                            activeMap={activeMap}
                            currentMap={currentMap}
                            draftMap={draftMap}
                            mapLoading={mapLoading}
                            editedByOtherUsers={editedByOtherUsers}
                            onActivate={this.activateDraft}
                            onDiscard={this.discardDraft}
                            onNewDraft={this.newDraft}
                            onShowDraft={this.showDraft}
                        />
                        {(activeMap && currentMap) ? mapAdditionalActions : <EITExport />}
                        {(activeMap && currentMap) && (
                            <MapViewers
                                map={currentMap}
                                onEditFromOtherUser={this.handleEditFromOtherUser}
                                onMapLocked={this.handleMapLocked}
                            />
                        )}
                    </Box>
                </ContextNavbar>
                <Spinner loading={loading} />
                {currentMap && (
                    <StyledBox
                        height="calc(100vh - 115px)"
                        margin={{ top: CONTEXT_NAVBAR_HEIGHT }}
                        pad="small"
                    >
                        {children({
                            map: currentMap,
                            permissions,
                            zoom,
                            center,
                            setPosition: this.setPosition,
                            setMapLoading: this.setMapLoading,
                        })}
                    </StyledBox>
                )}
            </Box>
        );
    }
}

MapEditorSelector.defaultProps = {
    mapAdditionalActions: null,
};


export default injectIntl(MapEditorSelector);
